const deepClone = (value) => JSON.parse(JSON.stringify(value))

const cleanId = (value) => String(value || '').trim().toLowerCase()

export const columnStyle = (widthPercent, options = {}) => ({
  widthPercent,
  backgroundColor: options.backgroundColor || 'transparent',
  backgroundImageFileId: null,
  backgroundImageUrl: options.backgroundImageUrl || '',
  backgroundSize: options.backgroundSize || 'cover',
  backgroundPosition: options.backgroundPosition || 'center',
  backgroundRepeat: 'no-repeat',
  height: options.height ?? 120,
  heightUnit: 'px',
  paddingX: options.paddingX ?? 0,
  paddingY: options.paddingY ?? 0,
  contentJustify: options.contentJustify || 'start',
  contentAlign: options.contentAlign || 'start',
})

const baseElementProps = (columnIndex, options = {}) => ({
  elementWidthPercent: options.width ?? 100,
  elementHeight: options.height ?? 72,
  elementHeightUnit: 'px',
  elementMargin: options.margin ?? 0,
  elementPadding: 0,
  elementPaddingX: options.paddingX ?? 0,
  elementPaddingY: options.paddingY ?? 0,
  elementAlign: options.align || 'left',
  elementBackgroundColor: options.backgroundColor || 'transparent',
  rowElementColumnIndex: columnIndex,
})

export const header = (columnIndex, content, options = {}) => ({
  type: 'header',
  props: {
    ...baseElementProps(columnIndex, { height: options.height ?? 72, ...options }),
    text: content,
    level: options.level || 'h2',
    size: options.size || 42,
    textSettings: {
      fontSize: options.size || 42,
      fontWeight: options.weight || 800,
      lineHeight: options.lineHeight || 1.05,
      textColor: options.color || '#18181B',
      textAlign: options.textAlign || options.align || 'left',
      linkAddress: '',
      openInNewTab: false,
    },
  },
})

export const text = (columnIndex, content, options = {}) => ({
  type: 'text',
  props: {
    ...baseElementProps(columnIndex, { height: options.height ?? 48, ...options }),
    content,
    textSettings: {
      fontSize: options.size || 16,
      fontWeight: options.weight || 400,
      lineHeight: options.lineHeight || 1.55,
      textColor: options.color || '#52525B',
      textAlign: options.textAlign || options.align || 'left',
      linkAddress: options.linkAddress || '',
      openInNewTab: options.openInNewTab === true,
    },
  },
})

export const image = (columnIndex, imageUrl, options = {}) => ({
  type: 'image',
  props: {
    ...baseElementProps(columnIndex, { height: options.height ?? 280, ...options }),
    imageFileId: null,
    imageUrl,
    imageWidth: options.imageWidth ?? 100,
    imageAlign: options.align || 'center',
    imageBorderRadius: options.radius ?? 20,
    imageShadowStrength: options.shadow ?? 14,
    imageLinkAddress: options.href || '',
    imageOpenInNewTab: options.openInNewTab === true,
  },
})

export const video = (columnIndex, videoUrl, options = {}) => ({
  type: 'video',
  props: {
    ...baseElementProps(columnIndex, { height: options.height ?? 420, ...options }),
    videoType: 'upload',
    videoFileId: null,
    videoUrl,
    videoPlaybackUrl: videoUrl,
    videoExternalUrl: '',
    videoEmbedCode: '',
    videoFileName: options.fileName || 'Template preview video',
    videoFileMimeType: options.mimeType || 'video/webm',
    videoThumbnailFileId: null,
    videoThumbnailUrl: options.posterUrl || '',
    videoAutoPreviewUrl: options.posterUrl || '',
    videoPosterUrl: options.posterUrl || '',
    videoBorderRadius: options.radius ?? 20,
    videoControlColor: options.controlColor || '#EC4899',
  },
})

export const bulletList = (columnIndex, items, options = {}) => ({
  type: 'bullet-list',
  props: {
    ...baseElementProps(columnIndex, { height: options.height ?? 180, ...options }),
    bulletItems: (items || []).map((item) => ({ text: String(item || '') })),
    bulletIcon: options.icon || 'check-circle',
    bulletIconColor: options.iconColor || '#22C55E',
    bulletTextColor: options.color || '#E4E4E7',
    bulletFontSize: options.size || 17,
    bulletGap: options.gap ?? 14,
    bulletLineHeight: options.lineHeight || 1.5,
  },
})

export const optinForm = (columnIndex, options = {}) => ({
  type: 'optin-form',
  props: {
    ...baseElementProps(columnIndex, { height: options.height ?? 390, ...options }),
    optinId: `template_${String(options.id || 'lead').replace(/[^a-z0-9_-]/gi, '_')}`,
    optinHeadline: options.headline || 'Get the free guide',
    optinDescription: options.description || 'Enter your details and we will send it straight to your inbox.',
    optinShowLabels: options.showLabels !== false,
    optinFields: options.fields || [
      { key: 'firstName', type: 'text', label: 'First name', placeholder: 'Your first name', showLabel: true, required: false },
      { key: 'email', type: 'email', label: 'Email address', placeholder: 'you@example.com', showLabel: true, required: true },
    ],
    optinButtonLabel: options.buttonLabel || 'Send me the guide',
    optinSuccessMessage: options.successMessage || 'Thanks! Check your inbox for the guide.',
    optinRedirectUrl: options.redirectUrl || '',
    optinRedirectNewTab: false,
    optinSendToIntegration: false,
    optinFieldGap: options.fieldGap ?? 12,
    optinInputHeight: options.inputHeight ?? 50,
    optinCornerRadius: options.radius ?? 12,
    optinLabelColor: options.labelColor || '#E4E4E7',
    optinInputBackgroundColor: options.inputBackgroundColor || '#FFFFFF',
    optinInputTextColor: options.inputTextColor || '#18181B',
    optinInputBorderColor: options.inputBorderColor || '#D4D4D8',
    optinButtonBackgroundColor: options.buttonBackgroundColor || '#4F46E5',
    optinButtonTextColor: options.buttonTextColor || '#FFFFFF',
    optinBackgroundColor: options.backgroundColor || '#18181B',
  },
})

export const button = (columnIndex, label, options = {}) => ({
  type: 'button',
  props: {
    ...baseElementProps(columnIndex, { height: options.height ?? 66, ...options }),
    // Button color belongs to the control, not its full-width editor wrapper.
    elementBackgroundColor: 'transparent',
    buttonText: label,
    button_text: label,
    content: label,
    buttonBackgroundColor: options.backgroundColor || '#EC4899',
    buttonTextColor: options.color || '#FFFFFF',
    buttonTextSize: 100,
    buttonFontSize: options.fontSize || 16,
    buttonActionType: 'go_to',
    buttonGoToType: 'custom_link',
    buttonFeatureSlug: '',
    buttonLinkAddress: options.href || '#',
    buttonOpenInNewTab: options.openInNewTab === true,
    buttonFileId: null,
    buttonFileUrl: '',
    buttonFileName: '',
    buttonFileMimeType: '',
    buttonWidth: options.buttonWidth ?? 32,
    buttonPaddingX: options.buttonPaddingX ?? 24,
    buttonPaddingY: options.buttonPaddingY ?? 14,
    buttonBorderRadius: options.radius ?? 10,
    buttonAlign: options.align || 'left',
  },
})

export const row = (columns, elements, options = {}) => ({
  type: 'row',
  props: {
    rowColumnCount: columns.length,
    rowColumnGutter: options.gutter ?? 28,
    rowWidthPercent: options.width ?? 100,
    rowHeight: options.height ?? 320,
    rowHeightUnit: 'px',
    rowPadding: 0,
    rowPaddingX: options.paddingX ?? 32,
    rowPaddingY: options.paddingY ?? 32,
    rowMargin: options.margin ?? 0,
    rowAlign: options.align || 'center',
    rowBackgroundColor: options.backgroundColor || 'transparent',
    rowBackgroundImageFileId: null,
    rowBackgroundImageUrl: options.backgroundImageUrl || '',
    rowBackgroundSize: options.backgroundSize || 'cover',
    rowBackgroundPosition: options.backgroundPosition || 'center',
    rowBackgroundRepeat: 'no-repeat',
    rowColumnStyles: columns,
    elements,
  },
})

export const section = (rows, options = {}) => ({
  type: 'section',
  props: {
    sectionWidthPercent: 100,
    sectionHeight: options.height ?? 320,
    sectionHeightUnit: 'px',
    sectionPadding: 0,
    sectionPaddingX: options.paddingX ?? 24,
    sectionPaddingY: options.paddingY ?? 24,
    sectionMargin: 0,
    sectionAlign: 'left',
    sectionBackgroundColor: options.backgroundColor || '#FFFFFF',
    sectionBackgroundImageFileId: null,
    sectionBackgroundImageUrl: options.backgroundImageUrl || '',
    sectionBackgroundSize: options.backgroundSize || 'cover',
    sectionBackgroundPosition: options.backgroundPosition || 'center',
    sectionBackgroundRepeat: 'no-repeat',
    rows,
  },
})

export const document = (title, description, sections) => ({
  version: 2,
  title,
  sections,
  seo: { title, description },
})

export const analyzePageTemplateDocument = (doc) => {
  const stats = { sections: 0, rows: 0, columns: 0, elements: 0, images: 0, videos: 0, forms: 0, headings: 0, buttons: 0 }
  const sections = Array.isArray(doc?.sections) ? doc.sections : []
  stats.sections = sections.length
  sections.forEach((sectionNode) => {
    const rows = Array.isArray(sectionNode?.props?.rows) ? sectionNode.props.rows : []
    stats.rows += rows.length
    rows.forEach((rowNode) => {
      stats.columns += Array.isArray(rowNode?.props?.rowColumnStyles) ? rowNode.props.rowColumnStyles.length : 0
      const elements = Array.isArray(rowNode?.props?.elements) ? rowNode.props.elements : []
      stats.elements += elements.length
      elements.forEach((element) => {
        if (element?.type === 'image') stats.images += 1
        if (element?.type === 'video') stats.videos += 1
        if (element?.type === 'optin-form') stats.forms += 1
        if (element?.type === 'header') stats.headings += 1
        if (element?.type === 'button') stats.buttons += 1
      })
    })
  })
  return stats
}

export const validatePageTemplateDocument = (doc) => {
  const errors = []
  if (!doc || typeof doc !== 'object') return ['Template document is missing.']
  if (!Array.isArray(doc.sections) || doc.sections.length === 0) errors.push('Template must include at least one section.')
  ;(doc.sections || []).forEach((sectionNode, sectionIndex) => {
    if (sectionNode?.type !== 'section') errors.push(`Section ${sectionIndex + 1} must use the section block type.`)
    ;(sectionNode?.props?.rows || []).forEach((rowNode, rowIndex) => {
      if (rowNode?.type !== 'row') errors.push(`Section ${sectionIndex + 1}, row ${rowIndex + 1} must use the row block type.`)
      const columnCount = Array.isArray(rowNode?.props?.rowColumnStyles) ? rowNode.props.rowColumnStyles.length : 0
      ;(rowNode?.props?.elements || []).forEach((element, elementIndex) => {
        const columnIndex = Number(element?.props?.rowElementColumnIndex || 0)
        if (columnIndex < 0 || columnIndex >= Math.max(columnCount, 1)) errors.push(`Element ${elementIndex + 1} in section ${sectionIndex + 1}, row ${rowIndex + 1} targets an invalid column.`)
        if (element?.type === 'image' && !element?.props?.imageFileId && !element?.props?.imageUrl) errors.push(`Image ${elementIndex + 1} in section ${sectionIndex + 1}, row ${rowIndex + 1} has no source.`)
      })
    })
  })
  return errors
}

export const definePageTemplate = (definition) => {
  const id = cleanId(definition?.id)
  if (!id) throw new Error('Page template id is required.')
  if (typeof definition?.build !== 'function') throw new Error(`Page template "${id}" needs a build function.`)
  return Object.freeze({
    id,
    name: String(definition.name || id),
    category: String(definition.category || 'General'),
    description: String(definition.description || ''),
    tags: Object.freeze([...(definition.tags || [])]),
    build: definition.build,
  })
}

export const createPageTemplateRegistry = (definitions, options = {}) => {
  const fallbackId = cleanId(options.fallbackId || definitions?.[0]?.id)
  const byId = new Map()
  const templates = (definitions || []).map((definition) => {
    if (byId.has(definition.id)) throw new Error(`Duplicate page template id: ${definition.id}`)
    const doc = definition.build()
    const errors = validatePageTemplateDocument(doc)
    if (errors.length) throw new Error(`Invalid page template "${definition.id}": ${errors.join(' ')}`)
    const catalogEntry = Object.freeze({
      id: definition.id,
      name: definition.name,
      category: definition.category,
      description: definition.description,
      tags: definition.tags,
      stats: Object.freeze(analyzePageTemplateDocument(doc)),
    })
    byId.set(definition.id, definition)
    return catalogEntry
  })

  return Object.freeze({
    templates: Object.freeze(templates),
    build(templateId) {
      const definition = byId.get(cleanId(templateId)) || byId.get(fallbackId)
      if (!definition) throw new Error(`Unknown page template: ${templateId}`)
      return deepClone(definition.build())
    },
  })
}
